import { useEffect, useRef, useState } from "react";
import type { Discrepancy } from "../api/types";
import {
  MEANING_LABEL, FLAG_CATEGORIES, CATEGORY_LABEL, RECATEGORIZABLE,
  type MeaningClass, type ReviewStatus, type FlagCategory,
} from "../lib/reportStore";
import { formatTime } from "../lib/format";
import { SHOW_MEANING_ANALYSIS } from "../lib/flags";
import type { ScriptMark } from "../lib/scriptNotes";
import { ScriptNoteLine, ScriptNotePanel } from "./ScriptNoteCard";
import { Icon } from "./icons";
import { toast } from "./toast";

export interface ReviewedFinding {
  d: Discrepancy;
  key: string;
  signature: string;
  status: ReviewStatus;
  dismissedByRule: boolean; // dismissed via a "dismiss all identical" rule
  reason?: "script"; // dismissed as "Intended, per the script"
  category: FlagCategory; // effective category (user override ?? categoryOf(type))
  meaning: MeaningClass | null; // effective classification (manual override ?? AI auto)
  meaningIsAuto: boolean; // the effective value came from the AI, not a manual pick
  autoRationale: string | null; // AI's brief reason (when auto)
  headline: string; // concise one-liner (title)
  comment: string; // display body (under the headline)
  editText: string; // what the editor shows: manual = "title\nbody", else the comment
  // A pickup from an earlier proof of this file that this proof didn't find
  // (the file was re-proofed): shown so it can be played and judged again.
  kept?: boolean;
}


interface Props {
  items: ReviewedFinding[]; // already filtered + sorted
  counts: Record<string, number>;
  activeTypes: Set<string>;
  onToggleType: (t: string) => void;
  search: string;
  onSearch: (s: string) => void;
  showDismissed: boolean;
  onToggleShowDismissed: () => void;
  showFiltered: boolean;
  onToggleShowFiltered: () => void;
  filteredCount: number;
  selectedId: string | null;
  selectTick?: number; // bumped on each select → re-scroll the list to the flag
  activeId: string | null;
  onSelect: (id: string) => void;
  onDismiss: (key: string) => void;
  onDismissAll: (d: Discrepancy) => void;
  onPickup: (key: string) => void;
  onEditComment: (key: string, text: string) => void;
  onEditTitle: (key: string, text: string) => void;
  onSetMeaning: (key: string, value: MeaningClass | "") => void;
  onSetCategory: (key: string, value: FlagCategory) => void;
  autoEditId: string | null;
  // The script's marks beside each flag (by flag id) - see ScriptNoteCard.
  projectId: string;
  chapterId: string;
  scriptMarks: Map<string, ScriptMark[]>;
  scriptHidden: Set<string>; // flags whose script note was put away this session
  onHideScript: (id: string) => void;
  onIntended: (key: string) => void;
  markedCount: number; // open flags that meet a script mark
  onlyMarked: boolean;
  onToggleOnlyMarked: () => void;
}

export function FindingsList(props: Props) {
  const listRef = useRef<HTMLDivElement>(null);

  // Reverse sync: clicking a highlighted issue in the manuscript selects its
  // flag (selectedId), so scroll the list to that row. block:"nearest" means no
  // jump when the row is already visible (e.g. you clicked the row itself).
  useEffect(() => {
    if (!props.selectedId || !listRef.current) return;
    const row = listRef.current.querySelector(`[data-id="${props.selectedId}"]`);
    if (row) row.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [props.selectedId, props.selectTick]);

  return (
    <div className="findings">
      <div className="findings-toolbar">
        <div className="chips">
          {FLAG_CATEGORIES.map((t) => {
            const c = props.counts[t] || 0;
            const on = props.activeTypes.has(t);
            return (
              <button
                key={t}
                className={`chip m-${t} ${on ? "on" : ""}`}
                disabled={c === 0 && t !== "manual"}
                onClick={() => props.onToggleType(t)}
                title={`${CATEGORY_LABEL[t]} — ${c}`}
              >
                <span className="swatch" />
                <span className="chip-label">{CATEGORY_LABEL[t]}</span>
                <span className="chip-n">{c}</span>
              </button>
            );
          })}
        </div>
        <input
          className="input findings-search"
          placeholder="Search findings…"
          value={props.search}
          onChange={(e) => props.onSearch(e.target.value)}
        />
        {props.filteredCount > 0 && (
          <button
            className={`chip filtered-chip ${props.showFiltered ? "on" : ""}`}
            onClick={props.onToggleShowFiltered}
            title="Auto-filtered non-issues (punctuation, number wording, homophones). Hidden by default."
          >
            Filtered {props.filteredCount}
          </button>
        )}
        {(props.markedCount > 0 || props.onlyMarked) && (
          <button
            className={`sn-chip${props.onlyMarked ? " on" : ""}`}
            onClick={props.onToggleOnlyMarked}
            aria-pressed={props.onlyMarked}
            title={props.onlyMarked ? "Show every flag" : "Show only the flags where the script has a note, comment or mark"}
          >
            <Icon name="pen-line" size={13} />
            {props.markedCount} {props.markedCount === 1 ? "flag meets" : "flags meet"} a script mark
          </button>
        )}
        <label className="row" style={{ gap: 6, fontSize: 12 }}>
          <input type="checkbox" checked={props.showDismissed} onChange={props.onToggleShowDismissed} />
          dismissed
        </label>
      </div>

      <div className="findings-list scroll" ref={listRef}>
        {props.items.length === 0 ? (
          <div className="muted" style={{ padding: 20, textAlign: "center" }}>No findings match.</div>
        ) : (
          props.items.map((f) => (
            <FindingRow key={f.key + f.d.id} f={f} {...props} />
          ))
        )}
      </div>
    </div>
  );
}

export function FindingRow({
  f, selectedId, activeId, autoEditId, onSelect, onDismiss, onDismissAll, onPickup, onEditComment, onEditTitle, onSetMeaning, onSetCategory,
  projectId, chapterId, scriptMarks, scriptHidden, onHideScript, onIntended,
}: { f: ReviewedFinding } & Pick<Props,
  "selectedId" | "activeId" | "autoEditId" | "onSelect" | "onDismiss" | "onDismissAll" | "onPickup" | "onEditComment" | "onEditTitle" | "onSetMeaning" | "onSetCategory"
  | "projectId" | "chapterId" | "scriptMarks" | "scriptHidden" | "onHideScript" | "onIntended">) {
  const d = f.d;
  const marks = scriptMarks.get(d.id);
  const intended = f.status === "dismissed" && f.reason === "script";
  // The script's note opens with the flag; "Keep the flag" (or the chevron) puts it away.
  const scriptOpen = !!marks && d.id === selectedId && !scriptHidden.has(d.id);
  const isManual = d.type === ("manual" as any);
  // A freshly-created manual flag opens its editor automatically.
  const [editing, setEditing] = useState(() => d.id === autoEditId);
  const [draft, setDraft] = useState(f.editText);
  // Title editing (real flags only — a manual flag's title lives in its text).
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(f.headline);
  const startEditTitle = () => { setTitleDraft(f.headline); setEditingTitle(true); };
  const cls =
    `finding-row m-${f.category}` +
    (d.id === selectedId ? " selected" : "") +
    (d.id === activeId ? " active" : "") +
    (f.status === "dismissed" ? " dismissed" : "") +
    (f.status === "picked" ? " picked" : "") +
    (d.filtered ? " filtered" : "") +
    (marks ? " has-sn" : "") +
    (intended ? " intended" : "");

  const copy = () => {
    const text = f.editText || f.headline;
    navigator.clipboard?.writeText(text).then(
      () => toast("Comment copied", "success"),
      () => toast("Couldn't copy", "error")
    );
  };

  // Show the full contextual comment under the headline only when it adds
  // detail beyond the headline.
  const showComment = f.comment && f.comment.trim() !== f.headline.trim();

  return (
    <div className={cls} data-id={d.id} onClick={() => onSelect(d.id)}>
      <div className="finding-main">
        <div className="finding-head">
          {/* Manual flags can be set to any category (they default to "Manual");
              engine flags can be re-assigned among the detected categories. */}
          <select
            className={`type-tag type-tag-select m-${f.category}`}
            value={f.category}
            title={isManual ? "Set this flag's category" : "Change this flag's category (if it was mis-detected)"}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onSetCategory(f.key, e.target.value as FlagCategory)}
          >
            {(isManual ? FLAG_CATEGORIES : RECATEGORIZABLE).map((c) => (
              <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
            ))}
          </select>
          {d.page != null && <span className="muted">p.{d.page}</span>}
          {d.start_time != null && <span className="mono muted">{formatTime(d.start_time)}</span>}
          {f.status === "picked" && <span className="badge muted">picked</span>}
          {f.kept && (
            <span className="kept-badge" title="Picked up in an earlier proof of this file. This proof didn't find it — play it to hear whether it still holds, then keep it or let it go (✕).">
              Kept pickup · not in this proof
            </span>
          )}
          {intended && (
            <span className="sn-badge" title="Cleared: the narrator read what the script asked for. Not a pickup.">
              <Icon name="check" size={11} /> Intended, per the script
            </span>
          )}
          {f.dismissedByRule && <span className="badge muted" title="Dismissed via 'all identical'">rule</span>}
          {d.filtered && (
            <span className="badge muted" title={d.filtered.reason}>auto: {d.filtered.category}</span>
          )}
          {SHOW_MEANING_ANALYSIS && (
            <>
              {f.meaningIsAuto && (
                <span className="ai-badge" title={f.autoRationale ? `Deep Analysis: ${f.autoRationale}` : "Rated by Deep Analysis"}>Deep</span>
              )}
              <select
                className={`meaning-select impact-${f.meaning || "unset"}`}
                value={f.meaning || ""}
                title={
                  f.meaningIsAuto
                    ? `AI-classified${f.autoRationale ? `: ${f.autoRationale}` : ""} — change to override`
                    : "Classify how much this changes the meaning"
                }
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onSetMeaning(f.key, e.target.value as MeaningClass | "")}
              >
                <option value="">Rate impact…</option>
                <option value="substantial">{MEANING_LABEL.substantial} — meaning</option>
                <option value="slight">{MEANING_LABEL.slight} — meaning</option>
                <option value="none">{MEANING_LABEL.none}</option>
              </select>
            </>
          )}
        </div>
        {/* While editing a manual flag the textarea IS the title, so don't
            also show the (stale) headline above it. Real flags get an editable
            one-line title (double-click the headline or the ✎ beside it). */}
        {editingTitle && !isManual ? (
          <input
            className="input finding-title-input"
            value={titleDraft}
            autoFocus
            placeholder="Title… (Enter to save, Esc to cancel)"
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setTitleDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
              else if (e.key === "Escape") { setTitleDraft(f.headline); setEditingTitle(false); }
            }}
            onBlur={() => { setEditingTitle(false); onEditTitle(f.key, titleDraft); }}
          />
        ) : (
          !(editing && isManual) && (
            <div className="finding-title-row">
              <div
                className="finding-headline"
                title={isManual ? undefined : "Double-click to edit the title"}
                onDoubleClick={isManual ? undefined : (e) => { e.stopPropagation(); startEditTitle(); }}
              >
                {f.headline}
              </div>
              {!isManual && (
                <button
                  className="title-edit-btn"
                  title="Edit title"
                  onClick={(e) => { e.stopPropagation(); startEditTitle(); }}
                >
                  ✎
                </button>
              )}
            </div>
          )
        )}
        {editing ? (
          <textarea
            className="input"
            value={draft}
            autoFocus
            rows={3}
            placeholder={
              isManual
                ? "Title (first line) — Shift+Enter, then details. Enter to save."
                : "Comment… (Enter to save, Shift+Enter for a new line)"
            }
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.currentTarget.blur(); }
            }}
            onBlur={() => { setEditing(false); onEditComment(f.key, draft); }}
          />
        ) : (
          showComment && (
            <div className="finding-comment" onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}>
              {f.comment}
            </div>
          )
        )}
      </div>
      {/* No container-level stopPropagation: clicking the column's empty space
          falls through to the row (→ play). Only the buttons themselves swallow
          the click so an action doesn't also seek+play. */}
      <div className="finding-actions">
        <button className="btn ghost sm" title="Pick up (add to export)" onClick={(e) => { e.stopPropagation(); onPickup(f.key); }}>
          {f.status === "picked" ? "★" : "☆"}
        </button>
        <button className="btn ghost sm" title="Dismiss this flag" onClick={(e) => { e.stopPropagation(); onDismiss(f.key); }}>
          {f.status === "dismissed" && !f.dismissedByRule ? "↺" : "✕"}
        </button>
        <button
          className={`btn ghost sm dismiss-all${f.dismissedByRule ? " on" : ""}`}
          title={f.dismissedByRule
            ? "Restore all identical flags across the title"
            : "Dismiss ALL identical flags across the title"}
          onClick={(e) => { e.stopPropagation(); onDismissAll(d); }}
        >
          ⊗
        </button>
        <button className="btn ghost sm" title="Copy comment" onClick={(e) => { e.stopPropagation(); copy(); }}>⧉</button>
        <button className="btn ghost sm" title="Edit comment" onClick={(e) => { e.stopPropagation(); setEditing(true); }}>✎</button>
      </div>
      {marks && (scriptOpen ? (
        <ScriptNotePanel
          projectId={projectId}
          chapterId={chapterId}
          d={d}
          marks={marks}
          cleared={intended}
          onIntended={() => onIntended(f.key)}
          onHide={() => onHideScript(d.id)}
        />
      ) : (
        <ScriptNoteLine marks={marks} onOpen={() => {
          if (scriptHidden.has(d.id)) onHideScript(d.id);    // bring it back
          onSelect(d.id);
        }} />
      ))}
    </div>
  );
}
